/** The audit ledger — Phase F's *"ledger UI and auditor role end to end"*, as a table page
 * (ADR-0212, P10): a title, four tiles, the newest entries as a strip of links, one filter line and
 * one grid of entries.
 *
 * The chain has existed since Phase B and nothing showed it: every figure reached one entry through
 * its receipt, and `verify_ledger.py` said in its own docstring that *"an auditor cannot read a
 * chain from the product at all"*. This is that read. Its explanations - what the chain proves, what
 * is hashed, why a person is never named - are behind controls (`audit.*` in `infoText.ts`, and the
 * "What the chain proves" drawer, which carries the server's own caveats verbatim).
 *
 * ## The four rules this screen exists to respect
 *
 * **Rule 4 — no names, and the reason is about this screen rather than about privacy.** The hash
 * covers five things (`hash_covers`, from the server so this screen cannot misstate it) and a person
 * is not one of them, so a name beside an entry would be the only thing on the page that an auditor
 * re-performing the chain could not check, and the only thing that would read differently after an
 * erasure. So the Actor column is the **kind** and the **UUID**, and the control beside the filter
 * line says why - an unexplained UUID reads as an unfinished screen rather than as a guarantee.
 *
 * **Rule 3 — the receipt drawer is the single rendering of a chain**, so an entry about a figure
 * *opens* it. Nothing here re-renders the arithmetic.
 *
 * **Append-only** — there is no control here that writes anything. Export writes a file of the
 * entries on the page and nothing to the product; a "fix this" affordance on an immutable record
 * would be a lie about what the product can do.
 *
 * **Two questions, never one tick** (ADR-0020). Whether the stored chain adds up and whether it has
 * been committed anywhere it could not be rewritten are different facts and two different tiles.
 *
 * ## What the reader can check for themselves
 *
 * - **The page is whole.** Sequences are consecutive, and `gapsIn` says so from the rows the
 *   browser is holding rather than from a flag the server set about itself. The filter line narrows
 *   what the *table* shows and never what that check reads: it is computed over the rows held, so a
 *   filter cannot make a chain with a hole look whole.
 * - **The links hold.** The strip compares each entry's `prev_hash` with the hash of the one below.
 *
 * ## Who sees what
 *
 * `MAY_READ` is mirrored from `api/ledger.py`. A Contributor and a Viewer are told who reads the
 * chain rather than shown a page that refuses them (design rule 6). An **Auditor** whose engagement
 * covers a period or particular companies is told why a chain cannot be narrowed to it, and the
 * screen does not ask the server, which would refuse by design.
 */

import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { DownloadSimple, ShieldCheck } from "@phosphor-icons/react";
import { useState } from "react";

import { useMe } from "../api/auth";
import { useReader } from "../api/profile";
import { PersonName } from "./PersonRef";
import type { ChainEntry, ChainPage } from "../api/ledger";
import { gapsIn, useChain } from "../api/ledger";
import { Refused } from "../api/refusal";
import { ReadFailed } from "../components/Loaded";
import {
  ActionButton,
  Drawer,
  EMPTY_FILTERS,
  EmptyState,
  FilterLine,
  GridTable,
  HowDrawer,
  InfoPopover,
  StatusChip,
  TablePage,
  Tile,
  TileStrip,
} from "../components/ui";
import type { FilterState, GridColumn } from "../components/ui";
import {
  actorKindWords,
  ledgerEventWords,
  ledgerObjectWords,
  systemActorWords,
} from "../lib/customerWords";
import { instantDate, quantity, shortHash } from "../lib/format";
import type { Reader } from "../lib/format";
import { RequireSession } from "../shell/RequireSession";
import { WhyNote } from "./auditKit";
import { useGridSort } from "./gridSort";
import { Integrity } from "./LedgerIntegrity";
import { ChainStrip, VerifyChain } from "./LedgerVerify";
import { ANCHOR, CHECK, asText, atTime } from "./ledgerWords";
import { ScreenFrame } from "./ScreenFrame";
import { keepSearch } from "./search";
import type { ScreenSearch } from "./search";
import own from "./Ledger.module.css";
import styles from "./Screen.module.css";
import { useScreen } from "./useScreen";

/** Mirrored from `api/ledger.py`'s `MAY_READ`. A list rather than a negation, so a sixth role added
 * to the product defaults to being told who can rather than to seeing the chain. */
const MAY_READ: readonly string[] = ["admin", "approver", "auditor"];

export function LedgerScreen() {
  return (
    <RequireSession>
      <LedgerScreenBody />
    </RequireSession>
  );
}

/** What the server's refusal says, in this screen's words, for a reader `/auth/me` already told us
 * is engaged - so the sentence is there without the request. The remedy is the same fact the route
 * states (`api/ledger.py`): every figure still resolves to its own entry through its receipt, what was
 * filed is pinned by that filing's issued report, and reading the chain whole is a permission an
 * engagement would have to carry by name and none can yet. It is an open decision, not a setting. */
const ENGAGED_WORDS: Record<"period" | "companies", string> = {
  period:
    "An entry records when something was done, not which reporting period it was about, so there is " +
    "no window to apply your engagement to, and a chain shown with the entries outside it removed " +
    "could not be verified. This screen has not asked EmitIQ for it, because it would refuse. " +
    "Every figure you are engaged for still resolves to its own ledger entry through its receipt, and " +
    "what was filed is pinned by that filing's issued report: its exact bytes, its digest in this " +
    "chain and its ledger position, with a verification pack that can be checked without EmitIQ. " +
    "Reading the chain whole is a permission an engagement would have to carry by name, and none can " +
    "carry one yet; that is an open decision rather than a setting someone can change for you.",
  companies:
    "An entry records that something was done, not which company it was done for, and a chain shown " +
    "with entries removed could not be verified. This screen has not asked EmitIQ for it, " +
    "because it would refuse. Every figure you are engaged for still resolves to its own ledger " +
    "entry through its receipt, and what was filed is pinned by that filing's issued report. Reading " +
    "the chain whole is a permission an engagement would have to carry by name, and none can carry " +
    "one yet.",
};

const CSV_COLUMNS = [
  "seq",
  "type",
  "object_type",
  "object_id",
  "recorded_at",
  "actor_kind",
  "actor_id",
  "hash",
  "prev_hash",
] as const;

/** A cell that a spreadsheet will not read as a formula. */
function csvCell(value: string | number | null | undefined): string {
  const text = value === null || value === undefined ? "" : String(value);
  const safe = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
  return `"${safe.replace(/"/g, '""')}"`;
}

/** The entries on this page as a CSV, written in the browser from the rows already held. It reads
 * nothing new and writes nothing to the product, and it is named for what it holds: **this page**,
 * not the chain. The payload of an entry is not in it; the entry's own page shows that. */
function exportPage(rows: readonly ChainEntry[]): void {
  const lines = [CSV_COLUMNS.map(csvCell).join(",")];
  for (const row of rows) lines.push(CSV_COLUMNS.map((column) => csvCell(row[column])).join(","));
  const url = URL.createObjectURL(new Blob([`${lines.join("\r\n")}\r\n`], { type: "text/csv" }));
  const link = document.createElement("a");
  link.href = url;
  const newest = rows[0]?.seq;
  const oldest = rows[rows.length - 1]?.seq;
  link.download = `audit-ledger-${String(oldest)}-to-${String(newest)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

function LedgerScreenBody() {
  const screen = useScreen();
  const { data: me } = useMe();
  const search: Partial<ScreenSearch> = useSearch({ strict: false });
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterState>(EMPTY_FILTERS);
  const [proofOpen, setProofOpen] = useState(false);
  const [hashed, setHashed] = useState<ChainEntry | null>(null);

  //: `undefined` while `/auth/me` is in flight, and that is **not** "may not read": a screen that
  //: flashed the refusal sentence on every load would teach an Admin to ignore it.
  const mayRead = me ? MAY_READ.includes(me.role) : undefined;
  const before = search.before;
  //: **What narrows this reader, from `/auth/me`** - so the chain is not asked for at all when the
  //: server would refuse it by design. A request made only to be answered 403 is a failed call in
  //: every network log and in the browser gate's exemption list. While `/auth/me` is in flight the
  //: request waits for the answer (`mayRead === true` already does).
  const narrowedBy = me?.engagement ?? null;
  const page = useChain(before, mayRead === true && narrowedBy === null);

  /** The cursor is a navigation, so the browser's Back button walks the chain forward again and a
   * page of it is a link somebody can send. `replace: false` on purpose - each step back through
   * the chain is somewhere the reader has been. */
  const older = (seq: number) =>
    void navigate({ to: ".", search: (prev: Record<string, unknown>) => ({ ...prev, before: seq }) });
  const newest = () =>
    void navigate({ to: ".", search: (prev: Record<string, unknown>) => ({ ...prev, before: undefined }) });

  const refusal = page.error instanceof Refused ? page.error : null;
  // **Two codes, one screen** (gate 27). `chain_has_no_period` is an engagement over a window;
  // `chain_has_no_entity` is one over particular companies with no window.
  const engagement =
    refusal?.code === "chain_has_no_period" || refusal?.code === "chain_has_no_entity" ? refusal : null;
  //: Which of the two, whether `/auth/me` said so up front or the server refused after the fact.
  const narrowing: "period" | "companies" | null =
    narrowedBy ??
    (engagement === null ? null : engagement.code === "chain_has_no_entity" ? "companies" : "period");

  const data = page.data;
  const held = data?.rows ?? [];
  const needle = filter.search.trim().toLowerCase();
  const types = filter.values.type ?? [];
  const actors = filter.values.actor ?? [];
  const rows = held.filter(
    (row) =>
      (types.length === 0 || types.includes(row.type)) &&
      (actors.length === 0 || actors.includes(row.actor_kind)) &&
      (!needle ||
        `${String(row.seq)} ${row.type} ${ledgerEventWords(row.type)} ${row.object_type} ${ledgerObjectWords(row.object_type)} ${row.object_id} ${row.actor_id ?? ""}`
          .toLowerCase()
          .includes(needle)),
  );
  const { sorted, sort, setSort } = useGridSort(rows, {
    seq: (r) => r.seq,
    type: (r) => r.type,
    when: (r) => r.recorded_at,
  });
  // The filter's value is the exact type the chain hashed; its label is the customer's words (U8).
  const typeOptions = [...new Set(held.map((row) => row.type))].map((value) => ({ value, label: ledgerEventWords(value) }));
  const actorOptions = [...new Set(held.map((row) => row.actor_kind))].map((value) => ({ value, label: actorKindWords(value) }));

  // The page's own wholeness, over the rows **held** and never over the rows the filter shows.
  const gaps = gapsIn(held);

  const columns: GridColumn<ChainEntry>[] = [
    {
      id: "seq",
      header: "Entry",
      sortable: true,
      width: "76px",
      rowHeader: true,
      cell: (row) => (
        /* The way to this entry's own page: its typed payload, its links and what they point at. */
        <Link
          className={`num ${own.seqLink}`}
          to="/ledger/$seq"
          params={{ seq: String(row.seq) }}
          search={keepSearch}
          aria-label={`Open ledger entry ${String(row.seq)}`}
          data-testid="ledger-entry-open"
        >
          #{row.seq}
        </Link>
      ),
    },
    {
      id: "type",
      header: "Type",
      sortable: true,
      minWidth: "190px",
      value: (row) => ledgerEventWords(row.type),
      /* The words are the customer's (`lib/customerWords.ts`); the exact type the chain hashed stays one hover
         away and in the entry's own page, where an auditor will want it. */
      cell: (row) => (
        <span title={row.type} data-event-type={row.type}>
          {ledgerEventWords(row.type)}
        </span>
      ),
    },
    {
      id: "about",
      header: "About",
      width: "140px",
      value: (row) => `${ledgerObjectWords(row.object_type)} ${row.object_id.slice(0, 8)}`,
      cell: (row) => (
        <span title={`${row.object_type} ${row.object_id}`}>
          {ledgerObjectWords(row.object_type)} {row.object_id.slice(0, 8)}
        </span>
      ),
    },
    { id: "when", header: "When", sortable: true, width: "164px", cell: (row) => <RecordedAt at={row.recorded_at} /> },
    {
      id: "actor",
      header: "Actor",
      width: "140px",
      value: (row) => (row.actor_id ? `${actorKindWords(row.actor_kind)} ${row.actor_id}` : systemActorWords(row.actor_kind)),
      /* The kind travels with the identifier, because null is *the engine* and not *we did not
         record who* - two different facts that a blank cell would merge (design rule 5). Said as
         "EmitIQ (automatic)" and not "engine no person" (U8); the exact kind is the title. */
      cell: (row) => (
        <span
          data-testid="ledger-actor"
          title={row.actor_kind}
          data-actor-id={row.actor_id ?? undefined}
          data-actor-system={row.actor_id ? undefined : "true"}
        >
          {row.actor_id ? (
            <>
              {actorKindWords(row.actor_kind)}{" "}
              <span title={row.actor_id}>
                <PersonName userId={row.actor_id} fallback={row.actor_id} />
              </span>
            </>
          ) : (
            systemActorWords(row.actor_kind)
          )}
        </span>
      ),
    },
    {
      id: "hash",
      header: "Hash",
      width: "140px",
      value: (row) => row.hash,
      cell: (row) => (
        <span title={row.hash} data-testid="ledger-hash">
          {shortHash(row.hash)}
        </span>
      ),
    },
    {
      id: "prev",
      header: "Links back to",
      width: "168px",
      /* **Which entry this one links back to, and a way to it** - the chain read as a chain. The
         entry is found by the hash this one carries (the entry below it on the page), and falls
         back to the sequence before it for the oldest row, whose predecessor is on the next page.
         The first entry of a chain links back to nothing, and says so. */
      cell: (row) => {
        const below = held.find((candidate) => candidate.hash === row.prev_hash);
        const seq = below?.seq ?? row.seq - 1;
        return (
          <span data-testid="ledger-links-back" className={own.rowButtons}>
            {seq >= 1 ? (
              <Link
                className="num"
                to="/ledger/$seq"
                params={{ seq: String(seq) }}
                search={keepSearch}
                aria-label={`Links back to entry ${String(seq)}`}
                data-testid="ledger-links-back-open"
                data-links-back-seq={String(seq)}
              >
                #{seq}
              </Link>
            ) : (
              <span>First entry</span>
            )}
            <span title={row.prev_hash} data-testid="ledger-prev-hash">
              {shortHash(row.prev_hash)}
            </span>
          </span>
        );
      },
    },
    {
      id: "evidence",
      header: "Evidence",
      width: "196px",
      cell: (row) => (
        <span className={own.buttonColumn}>
          {row.calculation_id ? (
            <button
              type="button"
              className={styles.fixLink}
              data-testid="ledger-receipt"
              onClick={() => screen.openReceipt(row.calculation_id as string)}
            >
              Receipt
            </button>
          ) : null}
          <button
            type="button"
            className={styles.fixLink}
            data-testid="ledger-payload-toggle"
            aria-label={`What was hashed in entry ${String(row.seq)}: ${String(Object.keys(row.payload).length)} fields`}
            onClick={() => setHashed(row)}
          >
            What was hashed
          </button>
        </span>
      ),
    },
  ];

  // **A reader who cannot see the chain, or a read that did not answer, is a state and not a table.**
  if (mayRead === false) {
    return (
      <ScreenFrame screen={screen} title="Audit ledger" crumb="every entry, newest first" bodyScrolls>
        <TablePage
          icon={ShieldCheck}
          title="Approvals and audit"
          label="Audit ledger"
          actions={<InfoPopover infoKey="audit.ledger" />}
          state={
            <WhyNote
              testId="ledger-who-can"
              lead="An Admin, an Approver or an Auditor reads the chain whole."
              whyTitle="Why you cannot read the chain"
              why="Every figure you can see still resolves to its own ledger entry: open a figure and the receipt beside it shows the entry, the hash and the factor that priced it. The whole chain is an assurance view of the workspace rather than a figure, which is why it is a different question from the one a receipt answers."
            />
          }
        />
      </ScreenFrame>
    );
  }
  if (narrowing) {
    return (
      <ScreenFrame screen={screen} title="Audit ledger" crumb="every entry, newest first" bodyScrolls>
        <TablePage
          icon={ShieldCheck}
          title="Approvals and audit"
          label="Audit ledger"
          actions={<InfoPopover infoKey="audit.ledger" />}
          state={
            /* **Its own sentence, not a failed read.** A "try again" button here would be a control
               that predictably refuses - the defect UX-04 names. `message` carries the server's
               remedy too (`refusalFrom` joins the two), which is why the drawer prints one string.
               ADR-0048 changed what the remedy says: it names the chain permission as an unmade
               decision. */
            <WhyNote
              testId="ledger-no-period"
              asked={engagement ? "refused" : "not-asked"}
              lead={
                narrowing === "companies"
                  ? "Your engagement covers particular companies, and the ledger does not record which company an entry was for."
                  : "Your engagement covers a period, and the ledger has none to compare it to."
              }
              whyTitle="Why the chain is not shown to you"
              why={engagement ? engagement.message : ENGAGED_WORDS[narrowing]}
            />
          }
        />
      </ScreenFrame>
    );
  }

  return (
    <ScreenFrame
      screen={screen}
      title="Audit ledger"
      crumb="every entry, newest first, and what the chain does and does not prove"
      bodyScrolls
    >
      <TablePage
        icon={ShieldCheck}
        title="Approvals and audit"
        label="Audit ledger"
        actions={
          <>
            {/* Nothing in the chain, nothing to verify: a button for it would check an empty chain. */}
            {data && held.length > 0 ? <VerifyChain page={page} /> : null}
            {data && held.length > 0 ? (
              <ActionButton data-action="export" onClick={() => exportPage(held)}>
                <DownloadSimple size={14} weight="bold" aria-hidden="true" />
                Export page
              </ActionButton>
            ) : null}
            {data ? (
              <ActionButton data-testid="ledger-proof-open" onClick={() => setProofOpen(true)}>
                What the chain proves
              </ActionButton>
            ) : null}
            <HowDrawer infoKey="audit.ledger-basis" label="What is hashed" />
            <InfoPopover infoKey="audit.ledger" />
          </>
        }
        tiles={data ? <LedgerTiles data={data} onProof={() => setProofOpen(true)} /> : undefined}
        chart={data && held.length > 0 ? <ChainStrip rows={held} /> : undefined}
        filters={
          page.error || (data && held.length === 0 && before === undefined) ? undefined : (
            <FilterLine
              filters={[
                { id: "type", label: "Type", options: typeOptions },
                { id: "actor", label: "Actor", options: actorOptions },
              ]}
              state={filter}
              onChange={setFilter}
              resultCount={
                data
                  ? { shown: rows.length, total: held.length, noun: "entries on this page" }
                  : undefined
              }
              actions={
                <>
                  {data && held.length > 0 && gaps.length === 0 ? (
                    /* Checked from the rows held, not from a flag the server set about itself. */
                    <StatusChip tone="ok" size="sm" testId="ledger-whole">
                      {held.length} consecutive sequences
                    </StatusChip>
                  ) : null}
                  <InfoPopover infoKey="audit.ledger-actor" />
                </>
              }
            />
          )
        }
        table={
          page.error ? undefined : (
            <>
              {gaps.length > 0 ? (
                <p className={own.gap} role="alert" data-testid="ledger-gap">
                  <strong>
                    {gaps.length === 1
                      ? `Sequence #${String(gaps[0])} is missing from this page.`
                      : `${String(gaps.length)} sequences are missing from this page.`}
                  </strong>{" "}
                  A chain has no gaps. Do not treat the result above as a check of this chain.
                </p>
              ) : null}
              <GridTable
                caption="Ledger entries, newest first"
                columns={columns}
                rows={sorted}
                rowKey={(row) => String(row.seq)}
                rowAttributes={() => ({ "data-testid": "ledger-entry" })}
                loading={page.isPending}
                sort={sort}
                onSortChange={setSort}
                empty={
                  <EmptyState
                    compact
                    testId="ledger-empty"
                    icon={held.length > 0 ? undefined : ShieldCheck}
                    title={held.length > 0 ? "No entry on this page matches." : "No entries yet"}
                  >
                    {held.length > 0 ? null : "Every figure, approval and correction is recorded here."}
                  </EmptyState>
                }
                testId="ledger-chain"
              />
              {data && (held.length > 0 || before !== undefined) ? (
                <div className={own.pager}>
                  {data.next_before_seq === null ? (
                    <StatusChip tone="neutral" size="sm" testId="ledger-start">
                      First entry in the chain
                    </StatusChip>
                  ) : (
                    <button
                      type="button"
                      className={own.pagerButton}
                      data-testid="ledger-older"
                      onClick={() => older(data.next_before_seq as number)}
                    >
                      Older entries, from #{data.next_before_seq - 1}
                    </button>
                  )}
                  {before === undefined ? null : (
                    <button type="button" className={own.pagerButton} data-testid="ledger-newest" onClick={newest}>
                      Back to the newest
                    </button>
                  )}
                </div>
              ) : null}
            </>
          )
        }
        state={
          page.error ? (
            <ReadFailed of="The chain" error={page.error} onRetry={() => void page.refetch()} />
          ) : undefined
        }
      />

      {/* The two answers, kept apart (ADR-0020), with the server's own caveats verbatim, and what the
          hash covers - read from the server so this screen cannot misstate it. */}
      <Drawer
        open={proofOpen && data !== undefined}
        onClose={() => setProofOpen(false)}
        title="What the chain proves"
        width="lg"
        testId="ledger-proof-drawer"
      >
        {data ? (
          <>
            <Integrity check={data.check} />
            <p data-testid="ledger-covers">
              An entry&rsquo;s hash is computed over {data.hash_covers.join(", ")}. Everything else on
              a row - who recorded it, when, and what it was about - is protected by the table being
              insert-only, not by the chain.
            </p>
          </>
        ) : null}
      </Drawer>

      <Drawer
        open={hashed !== null}
        onClose={() => setHashed(null)}
        title={hashed ? `What was hashed in entry #${String(hashed.seq)}` : "What was hashed"}
        width="md"
        testId="ledger-payload-drawer"
      >
        {hashed ? <Payload row={hashed} /> : null}
      </Drawer>
    </ScreenFrame>
  );
}

/** The date and the time of an entry, in the reader's own preferences. Its own component so the
 * reader is read only once there is an entry to place in time: a screen that read the preferences on
 * mount would make a request of its own for a workspace with nothing in it, and a failure of that
 * read would then be rendered as nothing at all (`screenStates.test.tsx` found it). */
function RecordedAt({ at }: { at: string }) {
  const reader = useReader();
  const time = atTime(at, reader);
  return (
    <span>
      {instantDate(at, reader)}
      {time ? ` ${time}` : ""}
    </span>
  );
}

function LedgerTiles({ data, onProof }: { data: ChainPage; onProof: () => void }) {
  return data.rows.length > 0 ? <TilesWithReader data={data} onProof={onProof} /> : <Tiles data={data} onProof={onProof} reader={undefined} />;
}

function TilesWithReader({ data, onProof }: { data: ChainPage; onProof: () => void }) {
  return <Tiles data={data} onProof={onProof} reader={useReader()} />;
}

/** Four facts that answer *"can I trust this chain, and how current is it?"*. The words come from
 * the same lookups as the proof drawer, so the two cannot disagree. The two trust tiles open it. */
function Tiles({ data, onProof, reader }: { data: ChainPage; onProof: () => void; reader: Reader | undefined }) {
  const check = data.check;
  const newest = data.rows[0];
  const state = CHECK[check.state];
  const anchor = ANCHOR[check.anchor_state];
  return (
    <TileStrip label="The chain at a glance">
      <Tile value={quantity(String(data.entries))} label="Entries in the chain" testId="ledger-entries" />
      <Tile
        value={newest ? `#${String(newest.seq)}` : "None"}
        unit={newest ? instantDate(newest.recorded_at, reader) : undefined}
        label="Newest entry"
        testId="ledger-newest-entry"
      />
      <Tile
        value={state?.short ?? "Unrecognised"}
        unit={
          check.checked_from_seq === null || check.checked_to_seq === null
            ? undefined
            : `${String(check.entries_checked)} recomputed`
        }
        label="Does it add up"
        testId="ledger-integrity"
        onOpen={onProof}
        openLabel="Does it add up. What the chain proves"
      />
      <Tile
        value={anchor?.short ?? "Unrecognised"}
        unit={check.last_anchored_seq === null ? undefined : `up to #${String(check.last_anchored_seq)}`}
        label="Committed unalterably"
        testId="ledger-committed"
        onOpen={onProof}
        openLabel="Committed unalterably. What the chain proves"
      />
    </TileStrip>
  );
}

/** What an entry hashed: the typed payload, verbatim. The values are the hash preimage, so they are
 * shown at their stored scale; a key the entry type does not declare is named and never printed. */
function Payload({ row }: { row: ChainEntry }) {
  const keys = Object.keys(row.payload);
  return (
    <div data-testid="ledger-payload">
      {keys.length === 0 ? (
        <p>
          This entry carries no payload that the product can show. Either it was written with none,
          or its entry type no longer declares the keys it holds.
        </p>
      ) : (
        <dl className={own.pairs}>
          {keys.map((key) => (
            <div key={key} className={`${own.pair} ${row.prose_keys.includes(key) ? own.prose : ""}`}>
              <dt className={own.key}>{key}</dt>
              <dd className={own.value}>{asText(row.payload[key])}</dd>
            </div>
          ))}
        </dl>
      )}
      {row.undeclared_keys.length > 0 ? (
        <p className={own.undeclared} data-testid="ledger-undeclared">
          <strong>Not shown: {row.undeclared_keys.join(", ")}.</strong> This entry type does not
          declare those keys, so their values are named and not printed. Every payload is typed and
          bounded when it is written, and that is the only reason the values above can be shown as
          they are - a key outside the declaration has not been through that check.
        </p>
      ) : null}
    </div>
  );
}
